import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Por favor, escribe tu correo electrónico.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await resetPassword(email.trim());
      if (result.error) {
        setError(result.error);
        return;
      }
      setSent(true);
    } catch {
      setError("Ocurrió un error inesperado. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-store-2-line text-xl text-white"></i>
            </span>
            <span className="font-heading text-xl font-bold text-foreground-950">
              Vende<span className="text-primary-500">Ya</span>
            </span>
          </Link>
        </div>

        <div className="mb-6 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            Recuperar contraseña
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            Escribe tu correo y te enviaremos un enlace para restablecerla.
          </p>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-mail-send-line text-2xl text-secondary-600"></i>
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              Revisa tu correo
            </h2>
            <p className="text-sm leading-relaxed text-foreground-600">
              Te enviamos un enlace a{" "}
              <strong className="text-foreground-900">{email}</strong> para
              restablecer tu contraseña.
            </p>
            <Link
              to="/login"
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Volver a iniciar sesión
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-100 px-3 py-2.5 text-sm text-accent-900"
              >
                <i className="ri-error-warning-fill mt-0.5 text-accent-600"></i>
                <span className="leading-snug">{error}</span>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-foreground-700"
              >
                Correo electrónico
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                  <i className="ri-mail-line text-base"></i>
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting && (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              )}
              {submitting ? "Enviando..." : "Enviar enlace"}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-foreground-600">
          <Link
            to="/login"
            className="font-semibold text-primary-500 transition-colors hover:text-primary-600"
          >
            Volver a iniciar sesión
          </Link>
        </p>
      </div>
    </div>
  );
}